import { useState, useEffect, useRef, useCallback } from 'react';
import { TMDBMovie } from '../lib/tmdb';
import { getApiBaseUrl } from '../lib/api';

// YouTube IFrame API types
declare global {
  interface Window {
    YT: {
      Player: new (elementId: string, config: any) => any;
      PlayerState: {
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

export interface ValidatedMovie {
  movie: TMDBMovie;
  trailerVideoId: string;
  trailerDuration: number;
  validatedAt: number;
}

export interface UseTrailerRotationReturn {
  currentMovie: TMDBMovie;
  trailerVideoId: string | null;
  showTrailer: boolean;
  isLoadingTrailer: boolean;
  trailerError: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  handleNextMovie: () => void;
}

export function useTrailerRotation(): UseTrailerRotationReturn {
  // State management
  const [currentMovie, setCurrentMovie] = useState<TMDBMovie | null>(null);
  const [trailerVideoId, setTrailerVideoId] = useState<string | null>(null);
  const [showTrailer, setShowTrailer] = useState(false);
  const [trailerError, setTrailerError] = useState(false);
  const [isLoadingTrailer, setIsLoadingTrailer] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // Start as true since hero is at top of page
  const [youtubeApiLoaded, setYoutubeApiLoaded] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track if user has interacted with page
  const [silentAudioReady, setSilentAudioReady] = useState(false); // Track when silent audio is done (success or failure)
  const silentAudioRef = useRef<HTMLAudioElement | null>(null); // For silent audio workaround
  const silentAudioActivationRef = useRef<boolean>(false); // Track if silent audio successfully gained user activation
  const triedUnmutedAutoplayRef = useRef<boolean>(false); // Track if we attempted unmuted autoplay
  const previousPlayerStateRef = useRef<number>(-1); // Track previous player state to detect transitions
  
  // Movie pool and rotation state
  const [validatedMovies, setValidatedMovies] = useState<ValidatedMovie[]>([]);
  const [currentMovieIndex, setCurrentMovieIndex] = useState(0);
  const [isPoolLoaded, setIsPoolLoaded] = useState(false);
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const timeCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const nextMoviePrefetchRef = useRef<ValidatedMovie | null>(null);

  // Fisher-Yates shuffle algorithm
  const shuffleArray = useCallback((array: ValidatedMovie[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Load YouTube IFrame API script
  useEffect(() => {
    if (window.YT) return; // Already loaded
    
    // Set up global callback for YouTube API
    window.onYouTubeIframeAPIReady = () => {
      console.log('🎬 YouTube IFrame API loaded successfully');
      setYoutubeApiLoaded(true);
    };
    
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Fetch validated movie pool from backend
  const fetchValidatedMoviePool = useCallback(async () => {
    try {
      console.log('🎬 Fetching validated movie pool...');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tmdb/validated-movie-pool`);
      const data = await response.json();
      
      if (data.success && data.movies) {
        console.log(`✅ Loaded ${data.movies.length} validated movies`);
        const shuffledMovies = shuffleArray(data.movies);
        setValidatedMovies(shuffledMovies);
        setIsPoolLoaded(true);
        return shuffledMovies;
      } else {
        console.error('❌ Failed to fetch validated movie pool:', data.error);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching validated movie pool:', error);
      return [];
    }
  }, [shuffleArray]);

  // Initialize movie pool
  useEffect(() => {
    fetchValidatedMoviePool();
  }, [fetchValidatedMoviePool]);

  // Auto-interaction workaround: Programmatically trigger user activation on page load
  // Create hidden button and click it to gain user activation before player creation
  // This allows unmuted autoplay immediately on page load
  useEffect(() => {
    let activationGained = false;
    
    // Method 1: Hidden button click (Primary)
    const tryHiddenButtonClick = () => {
      try {
        const hiddenButton = document.createElement('button');
        hiddenButton.style.cssText = 'position: fixed; top: -9999px; left: -9999px; width: 1px; height: 1px; opacity: 0; pointer-events: none; z-index: -9999;';
        hiddenButton.setAttribute('aria-hidden', 'true');
        hiddenButton.setAttribute('tabindex', '-1');
        document.body.appendChild(hiddenButton);
        
        // Use requestAnimationFrame to click synchronously in same execution context
        requestAnimationFrame(() => {
          try {
            hiddenButton.click();
            activationGained = true;
            silentAudioActivationRef.current = true;
            console.log('✅ Hidden button click - user activation achieved');
          } catch (error) {
            console.log('⚠️ Hidden button click failed:', error);
          } finally {
            // Remove button immediately
            document.body.removeChild(hiddenButton);
          }
        });
      } catch (error) {
        console.log('⚠️ Failed to create hidden button:', error);
      }
    };
    
    // Method 2: Focus + Keyboard event (Fallback)
    const tryFocusKeyboardEvent = () => {
      if (activationGained) return;
      
      try {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'text';
        hiddenInput.style.cssText = 'position: fixed; top: -9999px; left: -9999px; width: 1px; height: 1px; opacity: 0; pointer-events: none; z-index: -9999;';
        hiddenInput.setAttribute('aria-hidden', 'true');
        hiddenInput.setAttribute('tabindex', '-1');
        document.body.appendChild(hiddenInput);
        
        requestAnimationFrame(() => {
          try {
            hiddenInput.focus();
            const keyboardEvent = new KeyboardEvent('keydown', {
              bubbles: true,
              cancelable: true,
              key: 'Enter',
              code: 'Enter',
              keyCode: 13
            });
            hiddenInput.dispatchEvent(keyboardEvent);
            
            if (!activationGained) {
              activationGained = true;
              silentAudioActivationRef.current = true;
              console.log('✅ Focus + keyboard event - user activation achieved');
            }
          } catch (error) {
            console.log('⚠️ Focus + keyboard event failed:', error);
          } finally {
            document.body.removeChild(hiddenInput);
          }
        });
      } catch (error) {
        console.log('⚠️ Failed to create hidden input:', error);
      }
    };
    
    // Try primary method immediately
    tryHiddenButtonClick();
    
    // Try fallback method after short delay
    setTimeout(() => {
      if (!activationGained) {
        tryFocusKeyboardEvent();
      }
    }, 50);
    
    // Update ready flag after attempts (success or failure)
    setTimeout(() => {
      setSilentAudioReady(true);
      if (activationGained) {
        console.log('✅ Auto-interaction succeeded - user activation available for hero video');
      } else {
        console.log('⚠️ Auto-interaction failed - will try silent audio fallback');
      }
    }, 100);
  }, []);

  // Silent audio workaround: Play silent audio to get user activation BEFORE player creation
  // This is a fallback if auto-interaction fails
  // Must play before creating player to enable unmuted autoplay from the start
  useEffect(() => {
    if (!silentAudioRef.current && !silentAudioActivationRef.current) {
      try {
        // Create a silent audio element
        const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=');
        silentAudio.volume = 0.001; // Very low volume but not 0 (0 might not work)
        silentAudio.loop = false;
        
        // Try to play it immediately - if this succeeds, we have user activation
        // This allows us to attempt unmuted autoplay when creating the player
        // IMPORTANT: Set ready flag when done (success OR failure) so player creation can proceed
        silentAudio.play().then(() => {
          if (!silentAudioActivationRef.current) {
            silentAudioActivationRef.current = true;
            console.log('🔇 Silent audio playing - user activation achieved (fallback), will attempt unmuted autoplay');
          }
          if (!silentAudioReady) {
            setSilentAudioReady(true);
          }
        }).catch(() => {
          // Silent fail - browser blocked it, will need real user interaction
          if (!silentAudioActivationRef.current) {
            silentAudioActivationRef.current = false;
            console.log('🔇 Silent audio blocked - will fall back to muted autoplay');
          }
          if (!silentAudioReady) {
            setSilentAudioReady(true);
          }
        });
        
        // Timeout fallback: If silent audio takes too long, proceed anyway (max 300ms wait)
        setTimeout(() => {
          if (!silentAudioReady) {
            console.log('🔇 Silent audio timeout - proceeding with player creation');
            setSilentAudioReady(true);
          }
        }, 300);
        
        silentAudioRef.current = silentAudio;
      } catch (error) {
        if (!silentAudioActivationRef.current) {
          silentAudioActivationRef.current = false;
        }
        if (!silentAudioReady) {
          setSilentAudioReady(true);
        }
        console.warn('⚠️ Silent audio workaround failed:', error);
      }
    }
  }, []);

  // Function to attempt unmute
  const attemptUnmute = useCallback((source: string) => {
    if (!playerRef.current) return false;
    
    try {
      const playerState = playerRef.current.getPlayerState();
      const wasPaused = playerState === window.YT.PlayerState.PAUSED;
      
      if (playerRef.current.isMuted && playerRef.current.unMute) {
        const isMuted = playerRef.current.isMuted();
        if (isMuted) {
          playerRef.current.unMute();
          console.log(`✅ Successfully unmuted video (${source})`);
          
          // If video was paused, restart it
          if (wasPaused && playerRef.current.playVideo) {
            setTimeout(() => {
              if (playerRef.current) {
                playerRef.current.playVideo();
                console.log('▶️ Restarted video after unmute');
              }
            }, 100);
          }
          return true;
        }
      }
    } catch (error) {
      console.warn(`⚠️ Failed to unmute (${source}):`, error);
      // If unmute failed and video is paused, try to resume muted
      if (playerRef.current && playerRef.current.playVideo) {
        try {
          playerRef.current.playVideo();
          console.log('▶️ Attempted to resume video (muted) after unmute failure');
        } catch (playError) {
          console.warn('⚠️ Failed to resume video:', playError);
        }
      }
    }
    return false;
  }, []);

  // Detect user interaction to enable unmuting (including scroll with workaround)
  // IMPORTANT: Keep listening for ALL clicks, not just first one
  // User might click modal, then come back to hero - we need to capture that activation
  useEffect(() => {
    const handleUserInteraction = (source: string) => {
      console.log(`👆 User interaction detected (${source})`, {
        hasUserInteracted,
        playerExists: !!playerRef.current,
        currentPlayerState: playerRef.current?.getPlayerState()
      });
      
      // Always set user interaction flag when detected
      if (!hasUserInteracted) {
        setHasUserInteracted(true);
      }
      
      // Always update silent audio activation ref (user clicks give activation)
      // This allows unmuting even if player was created before this click
      if (source === 'click' || source === 'touch' || source === 'keydown') {
        silentAudioActivationRef.current = true;
        console.log('✅ User activation captured - will attempt to unmute hero video');
      }
      
      // Try to unmute immediately if player exists and is muted
      if (playerRef.current) {
        const isMuted = playerRef.current.isMuted ? playerRef.current.isMuted() : true;
        const playerState = playerRef.current.getPlayerState();
        
        if (isMuted && (playerState === window.YT.PlayerState.PLAYING || playerState === window.YT.PlayerState.PAUSED)) {
          console.log(`🔊 Attempting to unmute hero video after user interaction (${source})`);
          setTimeout(() => {
            attemptUnmute(source);
          }, 100);
        }
      }
    };

    // Standard interactions (click, touch, keydown) - KEEP LISTENING (no once:true)
    // We want to capture clicks even after modal opens/closes
    const handleClick = (e: Event) => {
      // Only handle if click is not on modal or modal content
      const target = e.target as HTMLElement;
      if (!target.closest('[role="dialog"]') && !target.closest('.modal')) {
        handleUserInteraction('click');
      }
    };
    const handleTouch = () => handleUserInteraction('touch');
    const handleKey = () => handleUserInteraction('keydown');
    
    document.addEventListener('click', handleClick); // Removed once: true
    document.addEventListener('touchstart', handleTouch); // Removed once: true
    document.addEventListener('keydown', handleKey); // Removed once: true
    
    // Scroll with special handling - try unmute but resume if it pauses
    const handleScroll = () => {
      if (!hasUserInteracted && playerRef.current) {
        console.log('📜 Scroll detected - attempting unmute (may pause, will resume)');
        setHasUserInteracted(true);
        
        setTimeout(() => {
          const success = attemptUnmute('scroll');
          // If unmute failed and video paused, resume it muted
          if (!success && playerRef.current) {
            setTimeout(() => {
              const playerState = playerRef.current?.getPlayerState();
              if (playerState === window.YT.PlayerState.PAUSED && playerRef.current?.playVideo) {
                playerRef.current.playVideo();
                console.log('▶️ Resumed video (muted) after scroll unmute attempt');
              }
            }, 300);
          }
        }, 100);
      }
    };
    
    // Also try pointer events (some browsers treat these as valid interaction)
    const handlePointer = () => handleUserInteraction('pointer');
    
    window.addEventListener('scroll', handleScroll, { once: true, passive: true });
    window.addEventListener('wheel', handleScroll, { once: true, passive: true });
    document.addEventListener('pointerdown', handlePointer, { once: true });
    document.addEventListener('pointerup', handlePointer, { once: true });

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('touchstart', handleTouch);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      document.removeEventListener('pointerdown', handlePointer);
      document.removeEventListener('pointerup', handlePointer);
    };
  }, [hasUserInteracted, attemptUnmute]); // Keep hasUserInteracted in deps but we keep listening

  // Intersection Observer for visibility detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        console.log(`👁️ HeroBillboard visibility: ${entry.isIntersecting ? 'visible' : 'hidden'}`, {
          intersectionRatio: entry.intersectionRatio
        });
      },
      { 
        threshold: 0.5
      }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  // Prefetch next movie
  const prefetchNextMovie = useCallback(() => {
    if (validatedMovies.length === 0) return;
    
    const nextIndex = (currentMovieIndex + 1) % validatedMovies.length;
    const nextMovie = validatedMovies[nextIndex];
    
    if (nextMovie && !nextMoviePrefetchRef.current) {
      console.log(`🔮 Prefetching next movie: ${nextMovie.movie.title}`);
      nextMoviePrefetchRef.current = nextMovie;
    }
  }, [validatedMovies, currentMovieIndex]);

  // Handle next movie transition
  const handleNextMovie = useCallback(async () => {
    if (validatedMovies.length === 0) return;
    
    console.log(`🎬 Transitioning to next movie...`);
    
    // Clear current time check interval
    if (timeCheckIntervalRef.current) {
      clearInterval(timeCheckIntervalRef.current);
      timeCheckIntervalRef.current = null;
    }
    
    // Check if we have a prefetched movie ready
    if (nextMoviePrefetchRef.current) {
      console.log('⚡ Using prefetched movie for instant transition');
      const prefetchedMovie = nextMoviePrefetchRef.current;
      nextMoviePrefetchRef.current = null;
      
      setCurrentMovie(prefetchedMovie.movie);
      setTrailerVideoId(prefetchedMovie.trailerVideoId);
      setShowTrailer(true);
      setTrailerError(false);
      
      // Update index
      const nextIndex = (currentMovieIndex + 1) % validatedMovies.length;
      setCurrentMovieIndex(nextIndex);
      
      // Prefetch the movie after this one
      setTimeout(() => prefetchNextMovie(), 100);
      return;
    }
    
    // No prefetched movie, proceed with normal flow
    let nextIndex = currentMovieIndex + 1;
    if (nextIndex >= validatedMovies.length) {
      // Reshuffle and reset
      const shuffledMovies = shuffleArray(validatedMovies);
      setValidatedMovies(shuffledMovies);
      nextIndex = 0;
    }
    
    setCurrentMovieIndex(nextIndex);
    const nextMovie = validatedMovies[nextIndex];
    setCurrentMovie(nextMovie.movie);
    setTrailerVideoId(nextMovie.trailerVideoId);
    setShowTrailer(true);
    setTrailerError(false);
    
    // Prefetch the movie after this one
    setTimeout(() => prefetchNextMovie(), 100);
  }, [validatedMovies, currentMovieIndex, shuffleArray, prefetchNextMovie]);

  // Start checking video time
  const startTimeChecking = useCallback(() => {
    if (timeCheckIntervalRef.current) {
      clearInterval(timeCheckIntervalRef.current);
    }
    
    timeCheckIntervalRef.current = setInterval(() => {
      if (playerRef.current && playerRef.current.getCurrentTime) {
        const currentTime = playerRef.current.getCurrentTime();
        const duration = playerRef.current.getDuration();
        const endTime = duration - 3; // End 3 seconds before actual end
        
        if (currentTime >= endTime) {
          console.log(`⏰ Video reached end-3 seconds (${currentTime}/${duration}), transitioning...`);
          handleNextMovie();
        }
      }
    }, 500);
  }, [handleNextMovie]);

  // YouTube Player event handlers (defined before createPlayer to avoid circular dependency)
  const handlePlayerReady = useCallback((event: any) => {
    const currentState = event.target.getPlayerState();
    console.log('🎬 YouTube player ready!', {
      playerId: event.target.getVideoData().video_id,
      duration: event.target.getDuration(),
      isVisible,
      playerState: currentState,
      playerStateName: currentState === window.YT.PlayerState.PLAYING ? 'PLAYING' :
                       currentState === window.YT.PlayerState.PAUSED ? 'PAUSED' :
                       currentState === window.YT.PlayerState.BUFFERING ? 'BUFFERING' :
                       currentState === window.YT.PlayerState.CUED ? 'CUED' :
                       currentState === -1 ? 'UNSTARTED' : 'OTHER',
      isMuted: event.target.isMuted ? event.target.isMuted() : 'unknown',
      timestamp: new Date().toISOString()
    });
    playerRef.current = event.target;
    
    // Only start playing and time checking if visible
    if (isVisible) {
      console.log('🎬 Player ready and visible - starting playback and time checking');
      
      // Function to attempt play
      const attemptPlay = (attemptNumber: number) => {
        try {
          if (event.target && event.target.playVideo) {
            // Ensure video is muted before playing (for autoplay compliance)
            if (event.target.isMuted && !event.target.isMuted()) {
              event.target.mute();
              console.log(`🔇 Ensuring video is muted before play (attempt ${attemptNumber})`);
            }
            
            console.log(`🎬 Attempting to play video (attempt ${attemptNumber})`);
        event.target.playVideo();
            
            // Check player state after a short delay
            setTimeout(() => {
              const playerState = event.target.getPlayerState();
              const stateName = playerState === window.YT.PlayerState.PLAYING ? 'PLAYING' :
                                playerState === window.YT.PlayerState.PAUSED ? 'PAUSED' :
                                playerState === window.YT.PlayerState.BUFFERING ? 'BUFFERING' :
                                playerState === window.YT.PlayerState.CUED ? 'CUED' :
                                playerState === -1 ? 'UNSTARTED' : 'OTHER';
              
              console.log(`🎬 Player state after play attempt ${attemptNumber}:`, {
                state: stateName,
                stateCode: playerState,
                isMuted: event.target.isMuted ? event.target.isMuted() : 'unknown'
              });
              
              // If still not playing after attempt, try again
              if (playerState !== window.YT.PlayerState.PLAYING && 
                  playerState !== window.YT.PlayerState.BUFFERING &&
                  attemptNumber < 5) {
                console.log(`🔄 Video not playing yet, will retry...`);
              }
            }, 500);
          }
      } catch (error) {
          console.warn(`⚠️ Failed to start video (attempt ${attemptNumber}):`, error);
      }
      };
      
      // Update previous state ref
      previousPlayerStateRef.current = currentState;
      
      // Check if video is already playing (autoplay worked)
      // IMPORTANT: Don't clear flag on BUFFERING - wait for PLAYING state confirmation
      if (currentState === window.YT.PlayerState.PLAYING || 
          currentState === window.YT.PlayerState.BUFFERING) {
        const isMuted = event.target.isMuted ? event.target.isMuted() : true;
        
        // Only declare success if we're actually PLAYING, not just BUFFERING
        // BUFFERING can transition to UNSTARTED if unmuted autoplay gets blocked
        const isActuallyPlaying = currentState === window.YT.PlayerState.PLAYING;
        
        console.log('✅ Video autoplay initiated', {
          muted: isMuted,
          state: currentState === window.YT.PlayerState.PLAYING ? 'PLAYING' : 'BUFFERING',
          isActuallyPlaying,
          triedUnmuted: triedUnmutedAutoplayRef.current,
          // If unmuted autoplay worked, we need to confirm it's actually playing
          unmutedPossiblyWorked: !isMuted && triedUnmutedAutoplayRef.current && isActuallyPlaying
        });
        
        // DON'T clear flag here - wait for confirmation in handlePlayerStateChange
        // BUFFERING state is not enough - browser can block after BUFFERING → UNSTARTED
        
        startTimeChecking();
      } else if (currentState === -1) {
        // UNSTARTED - autoplay was blocked (likely unmuted autoplay blocked)
        if (triedUnmutedAutoplayRef.current) {
          console.log('🔄 Unmuted autoplay blocked on ready - retrying with muted autoplay');
          
          // Switch to muted and retry immediately
          setTimeout(() => {
            if (event.target.mute) {
              try {
                event.target.mute();
                triedUnmutedAutoplayRef.current = false; // Clear flag
                console.log('🔇 Muted video and retrying playback');
                attemptPlay(1);
                setTimeout(() => attemptPlay(2), 300);
                setTimeout(() => attemptPlay(3), 600);
              } catch (error) {
                console.warn('⚠️ Failed to mute and retry:', error);
              }
            }
          }, 100); // Very short delay
        }
        
        startTimeChecking();
      } else {
        // Other states - manually start it
        console.log('🔄 Autoplay didn\'t start, manually starting playback');
        attemptPlay(1);
        setTimeout(() => attemptPlay(2), 300);
        setTimeout(() => attemptPlay(3), 600);
        setTimeout(() => attemptPlay(4), 1000);
        startTimeChecking();
      }
    } else {
      console.log('🎬 Player ready but not visible - keeping paused');
      // Video is already paused due to autoplay: 0, no need to pause again
    }
  }, [isVisible, startTimeChecking]);

  const handlePlayerStateChange = useCallback((event: any) => {
    const stateNames = {
      [window.YT.PlayerState.ENDED]: 'ENDED',
      [window.YT.PlayerState.PLAYING]: 'PLAYING',
      [window.YT.PlayerState.PAUSED]: 'PAUSED',
      [window.YT.PlayerState.BUFFERING]: 'BUFFERING',
      [window.YT.PlayerState.CUED]: 'CUED',
      [-1]: 'UNSTARTED'
    };
    
    const previousState = previousPlayerStateRef.current;
    const currentState = event.data;
    previousPlayerStateRef.current = currentState;
    
    console.log('🎬 Player state changed:', {
      state: stateNames[currentState] || 'UNKNOWN',
      stateCode: currentState,
      previousState: stateNames[previousState] || 'UNKNOWN',
      transition: `${stateNames[previousState] || 'UNKNOWN'} → ${stateNames[currentState] || 'UNKNOWN'}`,
      triedUnmuted: triedUnmutedAutoplayRef.current,
      timestamp: new Date().toISOString()
    });
    
    // If video started playing, check if unmuted autoplay succeeded and confirm it
    if (event.data === window.YT.PlayerState.PLAYING) {
      if (!playerRef.current) return;
      
      const isMuted = playerRef.current.isMuted ? playerRef.current.isMuted() : true;
      
      // Check if we tried unmuted autoplay and verify it actually succeeded
      if (triedUnmutedAutoplayRef.current) {
        if (!isMuted) {
          // Unmuted autoplay appears to have worked - but wait for confirmation
          // Give it 500ms to ensure it stays playing and doesn't get blocked
          console.log('✅ Unmuted autoplay detected - waiting for confirmation (500ms)...');
          
          setTimeout(() => {
            if (playerRef.current) {
              const playerState = playerRef.current.getPlayerState();
              const stillMuted = playerRef.current.isMuted ? playerRef.current.isMuted() : true;
              
              // Confirm it's still playing and still unmuted
              if (playerState === window.YT.PlayerState.PLAYING && !stillMuted) {
                console.log('✅ Unmuted autoplay confirmed! (video still playing unmuted after 500ms)');
                triedUnmutedAutoplayRef.current = false; // Clear flag - success confirmed
              } else {
                console.log('⚠️ Unmuted autoplay confirmation failed - video may have been blocked', {
                  state: playerState,
                  muted: stillMuted
                });
                // Keep flag set in case we need to handle fallback
              }
            }
          }, 500);
        } else {
          // Video is muted - unmuted autoplay was blocked, try to unmute
          console.log('🔇 Video playing muted - unmuted autoplay was blocked, checking for user activation to unmute');
          
          // Try to unmute if we have any user activation (silent audio OR user interaction)
          const hasActivation = silentAudioActivationRef.current || hasUserInteracted;
          if (hasActivation) {
            setTimeout(() => {
              attemptUnmute('postPlayback-activation');
            }, 100);
          } else {
            console.log('⏳ No user activation yet - video will remain muted until interaction');
          }
        }
      } else {
        // Didn't try unmuted autoplay initially (player created without activation)
        // But now we might have activation (user clicked modal, etc.)
        const hasActivation = hasUserInteracted || silentAudioActivationRef.current;
        
        if (hasActivation && isMuted) {
          console.log('🔊 Player was created muted but we now have user activation - attempting unmute');
          setTimeout(() => {
            attemptUnmute('retroactive-activation');
          }, 100);
        }
      }
    }
    
    // If video was paused (possibly due to failed unmute), resume it
    if (event.data === window.YT.PlayerState.PAUSED && playerRef.current) {
      setTimeout(() => {
        try {
          const playerState = playerRef.current?.getPlayerState();
          // Resume if paused (keep muted if unmute failed)
          if (playerState === window.YT.PlayerState.PAUSED && playerRef.current?.playVideo) {
            // Ensure muted before resuming
            if (playerRef.current.mute && !playerRef.current.isMuted()) {
              playerRef.current.mute();
            }
            playerRef.current.playVideo();
            console.log('▶️ Resumed video (muted) after pause');
          }
        } catch (error) {
          console.warn('⚠️ Failed to resume video:', error);
        }
      }, 300);
    }
    
    // Handle UNSTARTED state - this means autoplay was blocked
    // If we tried unmuted autoplay and it's now UNSTARTED, immediately fall back to muted
    // Also detect transitions like BUFFERING → UNSTARTED (unmuted autoplay blocked mid-load)
    if (event.data === -1 && isVisible && playerRef.current) {
      const isUnmutedBlock = triedUnmutedAutoplayRef.current || 
                             (previousState === window.YT.PlayerState.BUFFERING || 
                              previousState === window.YT.PlayerState.PLAYING);
      
      if (isUnmutedBlock) {
        console.log('🚫 Unmuted autoplay was blocked - immediately falling back to muted autoplay', {
          triedUnmuted: triedUnmutedAutoplayRef.current,
          previousState: stateNames[previousState] || 'UNKNOWN'
        });
        
        // Immediately mute and retry (no delay - browser already blocked it)
        setTimeout(() => {
          if (playerRef.current) {
            const currentState = playerRef.current.getPlayerState();
            if (currentState === -1) {
              try {
                // Mute first
                if (playerRef.current.mute && !playerRef.current.isMuted()) {
                  playerRef.current.mute();
                  console.log('🔇 Muted video (unmuted autoplay was blocked)');
                }
                
                // Mark that we're no longer trying unmuted
                triedUnmutedAutoplayRef.current = false;
                
                // Retry playback muted
                if (playerRef.current.playVideo) {
                  playerRef.current.playVideo();
                  console.log('▶️ Retrying playback with muted autoplay');
                }
              } catch (e) {
                console.warn('⚠️ Failed to mute and retry playback:', e);
              }
            }
          }
        }, 100); // Very short delay just to ensure state is settled
      } else {
        // Generic UNSTARTED - just try to start it muted
        setTimeout(() => {
          if (playerRef.current) {
            const currentState = playerRef.current.getPlayerState();
            if (currentState === -1) {
              console.log('🔄 Video stuck in UNSTARTED, forcing muted playback');
              if (playerRef.current.mute && !playerRef.current.isMuted()) {
                playerRef.current.mute();
              }
              if (playerRef.current.playVideo) {
                try {
                  playerRef.current.playVideo();
                } catch (e) {
                  console.warn('⚠️ Failed to force playback:', e);
                }
              }
            }
          }
        }, 300);
      }
    }
    
    // If video ended, move to next movie
    if (event.data === window.YT.PlayerState.ENDED) {
      console.log('🎬 Video ended, moving to next movie');
      handleNextMovie();
    }
  }, [handleNextMovie]);

  // Helper function to create YouTube player
  const createPlayer = useCallback((elementId: string, videoId: string) => {
    // Destroy existing player if any
    if (playerRef.current) {
      console.log('🗑️ Destroying existing player');
      playerRef.current.destroy();
      playerRef.current = null;
    }
    
    // Capture current visibility state
    const currentVisibility = isVisible;
    
    // Create new player
    // Always try unmuted autoplay first - browsers allow this for users with site history (MEI)
    // If browser blocks it, we'll detect and switch to muted automatically
    // silentAudioActivationRef will be used later for post-playback unmuting if needed
    const hasSilentAudioActivation = silentAudioActivationRef.current; // Get current value at creation time
    const tryUnmutedAutoplay = currentVisibility && hasSilentAudioActivation; // Try unmuted if visible AND we have activation
    triedUnmutedAutoplayRef.current = tryUnmutedAutoplay; // Track if we're trying unmuted
    previousPlayerStateRef.current = -1; // Reset previous state
    
    // Get current origin for postMessage fix
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    
    console.log('🎬 Creating player - attempting unmuted autoplay', {
      tryUnmuted: tryUnmutedAutoplay,
      silentAudioActivation: hasSilentAudioActivation,
      currentVisibility,
      origin: currentOrigin,
      fallbackToMuted: 'will retry muted if blocked',
      trackingUnmutedAttempt: triedUnmutedAutoplayRef.current
    });
    
    const player = new window.YT.Player(elementId, {
      videoId: videoId,
      playerVars: {
        autoplay: currentVisibility ? 1 : 0, // Autoplay if visible
        mute: tryUnmutedAutoplay ? 0 : 1, // Try unmuted if we have activation, fallback to muted if blocked
        origin: currentOrigin, // Fix postMessage origin mismatch error
        controls: 0,
        start: 4,
        end: 120 - 3, // Default duration minus 3 seconds
        loop: 0,
        playlist: videoId,
        showinfo: 0,
        rel: 0,
        modestbranding: 1,
        enablejsapi: 1
      },
      events: {
        onReady: (event: any) => {
          console.log('🎬 YouTube player ready (new video)!', {
            playerId: event.target.getVideoData().video_id,
            duration: event.target.getDuration(),
            isVisible: currentVisibility
          });
          handlePlayerReady(event);
        },
        onStateChange: handlePlayerStateChange,
        onError: (event: any) => {
          console.error('🎬 YouTube player error:', event.data);
          // Handle errors like autoplay restrictions
          if (event.data === 101 || event.data === 150) {
            // Video not available or playback restricted
            // If we tried unmuted, fall back to muted
            console.log('🎬 Video playback restricted - checking if unmuted autoplay was blocked');
            setTimeout(() => {
              if (playerRef.current && isVisible) {
                try {
                  const playerState = playerRef.current.getPlayerState();
                  // If UNSTARTED, likely unmuted autoplay was blocked - retry muted
                  if (playerState === -1) {
                    console.log('🔄 Unmuted autoplay blocked, retrying muted...');
                    if (playerRef.current.mute) {
                      playerRef.current.mute();
                    }
                    playerRef.current.playVideo();
                    console.log('✅ Retried playback muted after unmuted autoplay block');
                  } else {
                    // Other error - just retry
                    playerRef.current.playVideo();
                    console.log('🔄 Retried playback after error');
                  }
                } catch (err) {
                  console.warn('Retry play failed:', err);
                }
              }
            }, 1000);
          }
        }
      }
    });
    
    console.log('✅ YouTube player created successfully');
  }, [handlePlayerReady, handlePlayerStateChange, isVisible]);

  // Initialize YouTube player when trailer video ID changes
  useEffect(() => {
    console.log('🎬 YouTube player initialization effect triggered', {
      trailerVideoId,
      hasYT: !!window.YT,
      showTrailer,
      trailerError,
      silentAudioReady,
      silentAudioActivation: silentAudioActivationRef.current
    });
    
    if (!trailerVideoId) {
      console.log('❌ No trailer video ID');
      return;
    }
    
    if (!window.YT) {
      console.log('❌ YouTube API not loaded yet');
      return;
    }
    
    // Wait for silent audio to complete (success or failure) before creating player
    // This ensures we have user activation if silent audio succeeded
    // MovieModal works because it's opened by user click (user gesture = user activation)
    // Hero component needs silent audio to gain activation first
    if (!silentAudioReady) {
      console.log('⏳ Waiting for silent audio to complete before creating player...');
      return;
    }
    
    // Wait for showTrailer to be true - ensures AnimatePresence has started the transition
    // This is important during movie transitions when AnimatePresence needs to unmount old element first
    if (!showTrailer) {
      console.log('⏳ Waiting for showTrailer to be true...');
      return;
    }
    
    const elementId = `youtube-player-${trailerVideoId}`;
    const element = document.getElementById(elementId);
    if (!element) {
      console.log('❌ YouTube player element not found:', elementId, '- polling for element');
      // Poll for element with exponential backoff (max 2 seconds)
      let attempts = 0;
      const maxAttempts = 20; // 20 * 100ms = 2 seconds max
      
      const pollForElement = () => {
        const retryElement = document.getElementById(elementId);
        if (retryElement) {
          console.log(`✅ Element found after ${attempts + 1} attempts, waiting for animation then creating player`);
          // Wait for AnimatePresence fade-in animation to complete (0.5s duration)
          // This ensures the element is fully visible before creating the player
          // YouTube autoplay can be blocked if element is not fully visible
          setTimeout(() => {
            createPlayer(elementId, trailerVideoId);
          }, 600); // 600ms > 500ms fade duration to ensure animation completes
        } else if (attempts < maxAttempts) {
          attempts++;
          const timeout = Math.min(100 * attempts, 500); // Exponential backoff, max 500ms
          setTimeout(pollForElement, timeout);
        } else {
          console.log('❌ Element still not found after maximum attempts');
        }
      };
      
      setTimeout(pollForElement, 50); // Start with 50ms
      return;
    }
    
    console.log('✅ Creating YouTube player for:', trailerVideoId, {
      silentAudioActivation: silentAudioActivationRef.current,
      userActivationAvailable: 'will attempt unmuted autoplay'
    });
    // Even when element exists immediately, wait for AnimatePresence animation
    // This handles the case where element exists but is still fading in during transition
    setTimeout(() => {
      createPlayer(elementId, trailerVideoId);
    }, 600); // 600ms to ensure fade-in animation completes
  }, [trailerVideoId, handlePlayerReady, handlePlayerStateChange, youtubeApiLoaded, createPlayer, silentAudioReady, showTrailer]);

  // Initialize with first movie when pool is loaded
  useEffect(() => {
    if (!isPoolLoaded || validatedMovies.length === 0) return;
    
    // Only initialize if we don't have a current movie yet
    if (!currentMovie) {
      console.log('🎬 Initializing first movie from validated pool');
      const firstMovie = validatedMovies[0];
      console.log('🎬 First movie data:', {
        title: firstMovie.movie.title,
        trailerVideoId: firstMovie.trailerVideoId,
        hasTrailerId: !!firstMovie.trailerVideoId
      });
      setCurrentMovie(firstMovie.movie);
      setTrailerVideoId(firstMovie.trailerVideoId);
      setShowTrailer(true);
      setTrailerError(false);
      
      // Debug: Check state after setting
      setTimeout(() => {
        console.log('🎬 State after setting:', {
          currentMovie: firstMovie.movie.title,
          trailerVideoId: firstMovie.trailerVideoId,
          showTrailer: true,
          isVisible: isVisible
        });
        
        // Check if YouTube player element exists
        const playerElement = document.getElementById(`youtube-player-${firstMovie.trailerVideoId}`);
        console.log('🎬 YouTube player element exists:', !!playerElement);
      }, 200);
      
      // Prefetch second movie
      setTimeout(() => prefetchNextMovie(), 100);
    }
  }, [isPoolLoaded, validatedMovies, currentMovie, prefetchNextMovie]);

  // Pause/resume video and time checking based on visibility
  useEffect(() => {
    if (!playerRef.current) {
      // If player isn't ready yet but visibility changed, we'll handle it in handlePlayerReady
      return;
    }
    
    if (isVisible) {
      console.log('👁️ Hero visible - resuming video and time checking');
      // Resume video playback - try multiple times to handle timing issues
      const attemptPlay = () => {
        if (playerRef.current && playerRef.current.playVideo) {
      try {
        playerRef.current.playVideo();
      } catch (error) {
        console.warn('Failed to resume video:', error);
      }
        }
      };
      
      // Try immediately
      attemptPlay();
      // Try again after short delay
      setTimeout(attemptPlay, 100);
      setTimeout(attemptPlay, 300);
      
      // Start time checking
      startTimeChecking();
    } else {
      console.log('👁️ Hero hidden - pausing video and stopping time checking');
      // Pause video playback
      if (playerRef.current && playerRef.current.pauseVideo) {
      try {
        playerRef.current.pauseVideo();
      } catch (error) {
        console.warn('Failed to pause video:', error);
        }
      }
      // Stop time checking
      if (timeCheckIntervalRef.current) {
        clearInterval(timeCheckIntervalRef.current);
        timeCheckIntervalRef.current = null;
      }
    }
  }, [isVisible, startTimeChecking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeCheckIntervalRef.current) {
        clearInterval(timeCheckIntervalRef.current);
      }
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
    };
  }, []);

  // Handle video load error
  const handleVideoError = useCallback(() => {
    console.error('Video failed to load, skipping to next movie');
    setShowTrailer(false);
    setTrailerError(true);
    // Skip immediately to next movie
    setTimeout(() => handleNextMovie(), 100);
  }, [handleNextMovie]);

  // Return hook interface
  return {
    currentMovie: currentMovie || {} as TMDBMovie,
    trailerVideoId,
    showTrailer,
    isLoadingTrailer,
    trailerError,
    containerRef,
    handleNextMovie
  };
}



